import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import last from 'lodash/last';
import isNil from 'lodash/isNil';

import { formatDate, parseDate } from '@common/utils/date';
import Dropdown from '@common/containers/fields/Dropdown';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { isString, isNumber } from '@common/utils/string';
import { Input } from './components/input';
import { Checkbox } from './components/checkbox';
import { Date } from './components/date';
import { Dropdown as FilterDropdown } from './components/dropdown';
import { NewTableFilterApplyType, NewTableFilterType, NewTableFilterValue } from '../../new-table.types';
import { MessageProps } from '@common/components/MessageBox.types';
import { ErrorMessage } from '@common/components/error-message';

import './filters.styles.scss';

type FilterId = string | number;

export type FiltersProps = {
	showMessage: (props: MessageProps) => void;
	options?: Array<string>;
	type: NewTableFilterType;
	outputDateFormat?: string;
	defaultValue?: NewTableFilterValue;
	moduleName: string;
	filters: Record<FilterId, string>;
	filterName?: string | number;
	onClose: () => void;
	onApply: (value?: NewTableFilterValue) => void;
};

enum FilterDtoProperties {
	whereClause1 = 'whereClause1',
	operator1 = 'operator1',
	whereClause2 = 'whereClause2',
	operator2 = 'operator2',
}

type FiltersState = {
	value: NewTableFilterValue;
};

type RenderFieldMetadata = {
	type: NewTableFilterType;
	className?: string;
	value?: string | number;
	metadata: {
		property: string;
	};
	onSave: (params: { value: string | number; metadata: { property: string } }) => void;
};

export class Filters extends React.Component<FiltersProps, FiltersState> {
	private andORPropertyName = 'andOR';
	static defaultProps = {
		defaultValue: {},
		type: NewTableFilterType.text,
		outputDateFormat: 'MM/DD/YYYY',
	};

	state = {
		value: this.props.defaultValue || {},
	};

	private processors: Partial<Record<NewTableFilterType, (x: string | number) => string | number>> = {
		[NewTableFilterType.date]: (x: string | number) => {
			if (x) {
				return formatDate(parseDate(x, this.props.outputDateFormat), { format: 'YYYY-MM-DD' });
			}

			return x;
		}
	};

	private normalizeNumberBeforeSave = (value: string) => {
    if (last(value) === '.') {
      value = value.trim().slice(0, -1);
    }
  
    return value;
  }

  private handleApply = () => {
		const { onApply, showMessage } = this.props;
		const defaultAndORValue = undefined;
		const processedValue = this.state.value
			? {
				[this.andORPropertyName]: defaultAndORValue,
				...this.state.value,
			}
			: undefined;

		const validators = [
			{
				isValid: () => {
					return (!isNil(processedValue?.operator1) && !isNil(processedValue?.whereClause1)) || (!isNil(processedValue?.operator2) && !isNil(processedValue?.whereClause2));
				},
				message: I18n.t('Please select at least one operator and one value'),
			},
			{
				isValid: () => {
					const parts = [
						[processedValue?.operator1, processedValue?.whereClause1],
						[processedValue?.operator2, processedValue?.whereClause2],
					];
					
					const valid = parts.every(([operator, value]) => {
						const bothEntered = !isNil(operator) && !isNil(value);
						const bothMissed = isNil(operator) && isNil(value);

						return bothEntered || bothMissed;
					});

					return valid;
				},
				message: I18n.t('Both operator and value should be selected'),
			},
			{
				isValid: () => {
					const allValuesSelected =
						!isNil(processedValue?.operator1) && 
						!isNil(processedValue?.whereClause1) &&
						!isNil(processedValue?.operator2) && 
						!isNil(processedValue?.whereClause2);

					return allValuesSelected ? !isNil(processedValue?.andOR) : true;
				},
				message: I18n.t('Please select And/Or'),
			},
		];

		const error = validators.find((x) => !x.isValid())?.message;

		if (error) {
			showMessage({
				header: I18n.t('Operation canceled'),
				content: <ErrorMessage>{error}</ErrorMessage>,
				size: 'small',
				confirmText: I18n.t('Ok')
			});

			return;
		}

		onApply(processedValue);
	}

	private renderFilterControls = (
		{ 
			typeProperty, 
			textProperty 
		}: { 
			typeProperty: FilterDtoProperties.operator1 | FilterDtoProperties.operator2; 
			textProperty: FilterDtoProperties.whereClause1 | FilterDtoProperties.whereClause2; 
		}) => {
		const { filters, type } = this.props;
		const { value } = this.state;
		const filterTypeValue = isNil(value[typeProperty]) ? undefined : filters[value[typeProperty]!];
		const processor = this.processors[type] || ((x: any) => x);
		const currentFilterValue = processor(value[textProperty] ?? '');

		return (
			<Flex alignItems="center" justify="space-between">
				<Dropdown
					className="NewTableFilters__filter-type"
					id="lbx-grid-filter-operator"
					selectedValue={filterTypeValue ?? ''}
					searchable={false}
					options={Object.values(filters)}
					optionsHeight={100}
					onValueChanged={this.handleFilterTypeChange(typeProperty)}
				/>

				{this.renderField(
					{
						type,
						className: "NewTableFilters__filter-text",
						value: currentFilterValue,
						metadata: {
							property: textProperty,
						},
						onSave: this.handleFilterChange,
					}
				)}
			</Flex>
		);
	}

	private handleFilterTypeChange = (property: FilterDtoProperties.operator1 | FilterDtoProperties.operator2) => (filterId: FilterId) => {
		const { filters } = this.props;

		const filterTypeId = Object.keys(filters).find((id) => {
			return filters[id] === filterId;
		});

		if (!isNil(filterTypeId)) {
			this.changeValue(property, filterTypeId);
		}
	}

	private handleFilterChange: RenderFieldMetadata['onSave'] = ({ value, metadata: { property } }) => {
		if (this.props.type === NewTableFilterType.date) {
			value = formatDate(value, { format: this.props.outputDateFormat });
		}

		this.changeValue(property, value);
	}

	private changeValue = (property: string, value: string | number) => {
		this.setState((state) => ({
			...state,
			value: {
				...state.value,
				[property]: value
			}
		}));
	}

	private handleApplyTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.persist();

		const { value } = e.target;

		this.changeValue(this.andORPropertyName, value);
	}

	private get applyTypes() {
		return [
			{ label: 'And', id: NewTableFilterApplyType.and },
			{ label: 'Or', id: NewTableFilterApplyType.or },
		];
	}

	private getFieldConfig = ({ type, options }: { type: NewTableFilterType; options?: Array<string> }) => {
		if (options?.length && options.length > 0 && type != NewTableFilterType.checkbox) {
			return {
				render: this.renderDropdown,
			};
		}

		const config = {
			[NewTableFilterType.text]: {
				render: this.renderInput
			},
			[NewTableFilterType.date]: {
				render: this.renderDate
			},
			[NewTableFilterType.number]: {
				render: this.renderNumber
			},
			[NewTableFilterType.integer]: {
				render: this.renderInteger
			},
			[NewTableFilterType.checkbox]: {
				render: this.renderCheckbox
			},
			[NewTableFilterType.dropdown]: {
				render: this.renderDropdown
			},
			[NewTableFilterType.image]: {
				render: this.renderInput
			},
			[NewTableFilterType.lookupTable]: {
				render: this.renderInput
			},
			[NewTableFilterType.table]: {
				render: this.renderInput
			},
			[NewTableFilterType.computed]: {
				render: this.renderInput
			},
			[NewTableFilterType.summary]: {
				render: this.renderInput
			},
			[NewTableFilterType.tableSummary]: {
				render: this.renderInput
			},
			[NewTableFilterType.hyperlink]: {
				render: this.renderInput
			},
		};

		return config[type];
	}

	private renderNumber = (data: RenderFieldMetadata) => {
    return (
      <Input
        normalizeBeforeSave={this.normalizeNumberBeforeSave}
        validate={isNumber}
        {...data}
				type="number"
      />
    );
  }

	private isInteger = (v: string) => {
		return Number.isInteger(Number(v));
	}

	private renderInteger = (data: RenderFieldMetadata) => {
    return (
      <Input
        normalizeBeforeSave={this.normalizeNumberBeforeSave}
        validate={this.isInteger}
        {...data}
				type="number"
      />
    );
  }

  private renderInput = (data: RenderFieldMetadata) => {
    return <Input {...data} />;
  }

  private renderDate = (data: RenderFieldMetadata) => {
    return <Date {...data} />;
  }

  private renderCheckbox = (data: RenderFieldMetadata) => {
    return <Checkbox {...data} />;
  }

  private renderDropdown = (data: RenderFieldMetadata) => {
    return <FilterDropdown options={this.props.options} {...data} />;
  }

  private renderField = (data: RenderFieldMetadata) => {
    const fieldConfig = this.getFieldConfig({ type: data.type, options: this.props.options });
    const render = fieldConfig ? fieldConfig.render : (() => null);

    return render(data);
  }

	componentDidMount() {
		if (!this.props.moduleName) {
			console.error('React Table: provide moduleName property for unique identifiers');
		}
	}
 
  render() {
    const { onClose, filterName } = this.props;
		const { value } = this.state;
		
    return (
      <Modal 
        show
        animation={false}
        backdrop="static"
        {...{ bsClass: "bootstrap modal" }}
        dialogClassName="NewTableFilters"
        {...{ onEscapeKeyDown: onClose }}
        onHide={onClose}
      >
        <ModalHeader closeButton onHide={onClose}>
          <ModalTitle id="msg-box-header">
            <span className="fa fa-filter" /> {I18n.t('Filter Column')}
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          <Flex className="NewTableFilters__main-control" justify="space-between" width="100%" alignItems="center">
						<span>{I18n.t('Show rows where')}{(isNumber(filterName) || isString(filterName)) ? ` ${filterName}` : ''}:</span>

						<Button id="btn-grid-filter-save" className="btn-primary" onClick={this.handleApply}>
							{I18n.t('Filter')}
						</Button>
          </Flex>

					{this.renderFilterControls(
						{
							textProperty: FilterDtoProperties.whereClause1,
							typeProperty: FilterDtoProperties.operator1,
						}
					)}

					<Flex alignItems="center" className="NewTableFilters__filter-apply-types">
						{this.applyTypes.map((type) => (
							<React.Fragment key={type.id}>
								<input 
									name="Table-apply-type"
									type="radio" 
									id={`Table-filter-type-${type.id}`} 
									className="NewTableFilters__filter-apply-types__input"
									value={type.id}
									checked={type.id === value.andOR}
									onChange={this.handleApplyTypeChange} 
								/>
								<label 
									htmlFor={`Table-filter-type-${type.id}`} 
									className="NewTableFilters__filter-apply-types__label"
								>
									{I18n.t(type.label)}
								</label>
							</React.Fragment>
						))}
					</Flex>

					{this.renderFilterControls(
						{
							textProperty: FilterDtoProperties.whereClause2,
							typeProperty: FilterDtoProperties.operator2,
						}
					)}
        </ModalBody>
      </Modal>
    );
  }
}
